import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isFeatureEnabled } from '@vp/ab-reader';
import { TabHeader, Tabs, TabsContents, TabsHeaders, tokens, useScreenClass } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { CombinedProject } from '../hooks/useCombinedProjects';
import { CombinedTabs } from './CombinedTabs';
import { StartNewProjectButton } from './StartNewProjectButton';

type TabName = 'current' | 'past' | 'draft' | 'myDesigners';

export const DashboardTabs = ({
    currentRequests,
    pastRequests,
    projectsError,
}: {
    currentRequests: CombinedProject[];
    pastRequests: CombinedProject[];
    projectsError?: Error;
}) => {
    const draftBriefsEnabled = isFeatureEnabled('design_services_draft_briefs');
    const myDesignersEnabled = isFeatureEnabled('design_services_my_designers');

    // Tabs currently visible, gated by feature flags.
    const tabIds: TabName[] = useMemo(() => {
        const ids: TabName[] = ['current', 'past'];
        if (myDesignersEnabled) {
            ids.push('myDesigners');
        }
        if (draftBriefsEnabled) {
            ids.push('draft');
        }
        return ids;
    }, [draftBriefsEnabled, myDesignersEnabled]);

    // Read selected tab from ?tab=<id>; ignore unknown or flag-disabled values.
    const getTabFromUrl = useCallback((): TabName | null => {
        if (typeof window === 'undefined') {
            return null;
        }
        const params = new URLSearchParams(window.location.search);
        const value = params.get('tab') as TabName | null;
        return value && tabIds.includes(value) ? value : null;
    }, [tabIds]);

    // Lazy init avoids a 'current' → URL-tab flicker on first paint.
    const [selectedTabId, setSelectedTabId] = useState<TabName>(() => getTabFromUrl() ?? 'current');
    const screenSize = useScreenClass();
    const isSmallScreen = screenSize === 'xs' || screenSize === 'sm';

    // Reset to 'current' if the selected tab is hidden by a flag flip.
    useEffect(() => {
        if (!tabIds.includes(selectedTabId)) {
            setSelectedTabId('current');
        }
    }, [selectedTabId, tabIds]);

    // Mirror selection into ?tab=; replaceState keeps history clean.
    const handleTabChange = (nextTab: TabName) => {
        setSelectedTabId(nextTab);
        const url = new URL(window.location.href);
        url.searchParams.set('tab', nextTab);
        if (url.toString() !== window.location.href) {
            window.history.replaceState({}, '', url);
        }
    };

    const tabHeaders = (
        <TabsHeaders display={'flex'}>
            <TabHeader tabId="current">{__('Current Projects')}</TabHeader>
            <TabHeader tabId="past">{__('Past Projects')}</TabHeader>
            {myDesignersEnabled && <TabHeader tabId="myDesigners">{__('My Designers')}</TabHeader>}
            {draftBriefsEnabled && <TabHeader tabId="draft">{__('Draft Briefs')}</TabHeader>}
        </TabsHeaders>
    );

    return (
        <>
            {isSmallScreen && (
                <div
                    style={{
                        width: '100%',
                        textAlign: 'center',
                        marginBottom: tokens.SwanSemSpaceBetweenSubsections,
                    }}
                >
                    <StartNewProjectButton />
                </div>
            )}
            <Tabs
                centerHeaders={isSmallScreen}
                selectedTabId={selectedTabId}
                onRequestTabChange={(requestedTabId) => handleTabChange(requestedTabId as TabName)}
                showDividingLine={false}
            >
                {!isSmallScreen ? (
                    <div style={{ display: 'flex' }}>
                        <span
                            style={{ display: 'inline-block', textAlign: 'left', alignSelf: 'end' }}
                        >
                            {tabHeaders}
                        </span>
                        <span
                            style={{
                                width: '100%',
                                display: 'inline-block',
                                textAlign: 'right',
                                alignSelf: 'center',
                            }}
                        >
                            <StartNewProjectButton />
                        </span>
                    </div>
                ) : (
                    tabHeaders
                )}
                <TabsContents>
                    <CombinedTabs
                        selectedTabId={selectedTabId}
                        currentRequests={currentRequests}
                        pastRequests={pastRequests}
                        projectsError={projectsError}
                    />
                </TabsContents>
            </Tabs>
        </>
    );
};
